import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, Users, Loader2, KeyRound, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersUsersPanel() {
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingShops, setLoadingShops] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [downloadingFor, setDownloadingFor] = useState(null);
  const [resetSending, setResetSending] = useState(null);
  const [tab, setTab] = useState('customers'); // customers | users

  const loadShops = async () => {
    try {
      const { data } = await axios.get(`${API}/superadmin/shops`, { withCredentials: true });
      const list = Array.isArray(data) ? data : (data?.shops || []);
      setShops(list);
    } catch (_) {
      toast.error('Errore caricamento negozi');
    } finally {
      setLoadingShops(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`${API}/superadmin/users`, {
        withCredentials: true,
        params: { search },
      });
      setUsers(data.users || []);
    } catch (_) {
      toast.error('Errore caricamento utenti');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => { loadShops(); }, []);
  useEffect(() => {
    if (tab === 'users') {
      const t = setTimeout(loadUsers, 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [tab, search]);

  const downloadCustomersCsv = async (shop) => {
    setDownloadingFor(shop.id);
    try {
      const res = await axios.get(`${API}/superadmin/shops/${shop.id}/customers`, {
        withCredentials: true,
        params: { export: 'csv' },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clienti-${shop.slug || shop.id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV scaricato');
    } catch (_) {
      toast.error('Errore download CSV');
    } finally {
      setDownloadingFor(null);
    }
  };

  const sendResetEmail = async (u) => {
    if (!window.confirm(`Inviare a ${u.email} il link per reimpostare la password?`)) return;
    setResetSending(u.id);
    try {
      const { data } = await axios.post(
        `${API}/superadmin/users/${u.id}/send-reset-email`,
        {},
        { withCredentials: true },
      );
      toast.success(data.message || 'Email inviata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio email');
    } finally {
      setResetSending(null);
    }
  };

  return (
    <div data-testid="customers-users-panel" className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
          Clienti & Utenti
        </h3>
        <div className="flex bg-[#F0EBE1] rounded-full p-0.5">
          <button
            data-testid="tab-customers"
            onClick={() => setTab('customers')}
            className={`px-3 py-1 text-xs font-semibold rounded-full ${tab === 'customers' ? 'bg-white shadow-sm text-[#C25934]' : 'text-[#5C554F]'}`}
          >
            Clienti per negozio
          </button>
          <button
            data-testid="tab-users"
            onClick={() => setTab('users')}
            className={`px-3 py-1 text-xs font-semibold rounded-full ${tab === 'users' ? 'bg-white shadow-sm text-[#C25934]' : 'text-[#5C554F]'}`}
          >
            Tutti gli utenti
          </button>
        </div>
      </div>

      {tab === 'customers' && (
        <div>
          {loadingShops ? (
            <div className="text-center py-6"><Loader2 className="animate-spin text-[#C25934] inline" size={20} /></div>
          ) : shops.length === 0 ? (
            <p className="text-xs text-[#877E78] text-center py-4">Nessun negozio</p>
          ) : (
            <div className="space-y-1.5">
              {shops.map(s => (
                <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#E8E3D9] hover:bg-[#F0EBE1]/40">
                  <Users size={14} className="text-[#877E78] flex-shrink-0" />
                  <span className="flex-1 text-sm truncate">{s.name}</span>
                  <Button
                    data-testid={`download-customers-${s.id}`}
                    onClick={() => downloadCustomersCsv(s)}
                    disabled={downloadingFor === s.id}
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[10px] rounded-full"
                  >
                    {downloadingFor === s.id
                      ? <Loader2 size={11} className="animate-spin" />
                      : <><Download size={11} className="mr-1" /> CSV clienti</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'users' && (
        <div>
          <div className="relative mb-2">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#877E78]" />
            <Input
              data-testid="user-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca per nome o email..."
              className="h-9 pl-7 text-xs"
            />
          </div>
          {loadingUsers ? (
            <div className="text-center py-6"><Loader2 className="animate-spin text-[#C25934] inline" size={20} /></div>
          ) : users.length === 0 ? (
            <p className="text-xs text-[#877E78] text-center py-4">Nessun utente</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#E8E3D9] hover:bg-[#F0EBE1]/40 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase flex-shrink-0 ${
                    u.role === 'superadmin' ? 'bg-[#4A6B53]/10 text-[#4A6B53]' :
                    u.role === 'admin' ? 'bg-[#C25934]/10 text-[#C25934]' :
                    'bg-[#F0EBE1] text-[#877E78]'
                  }`}>{u.role}</span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-[#2A2624]">{u.name || u.email.split('@')[0]}</p>
                    <p className="truncate text-[10px] text-[#877E78]">{u.email}</p>
                  </div>
                  <Button
                    data-testid={`send-reset-${u.id}`}
                    onClick={() => sendResetEmail(u)}
                    disabled={resetSending === u.id}
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[10px] rounded-full flex-shrink-0"
                    title="Invia link per cambio password"
                  >
                    {resetSending === u.id
                      ? <Loader2 size={11} className="animate-spin" />
                      : <><KeyRound size={11} className="mr-1" /> Reset</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
